import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Drawer from '@mui/material/Drawer';
import * as R from 'ramda';

import { useNavigate, useParams } from 'react-router-dom';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ConfirmationView from './ConfirmationView';
import Form from './Form';
import { useCreateNominationMutation } from '../nominationsEndpoints';

const initialState = {
  hasOnePossibleShipper: false,
  nominationMonth: { id: null, name: '', periodDate: '' },
  products: {},
  segment: null,
  shipper: null,
};

const CreateNominationWizard = ({
  isDrawerOpen,
  setIsDrawerOpen,
  copiedNomination,
  setCopiedNomination,
}) => {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false);
  const [nominationState, setNominationState] = useState(initialState);
  const [initialCopiedNominationState, setInitialCopiedNominationState] = useState();
  const [creationError, setCreationError] = useState('');
  const [step, setStep] = useState(1);
  const navigate = useNavigate();
  const { view } = useParams();

  const handleComplete = () => {
    setIsDrawerOpen(false);
    setCopiedNomination(null);
    setNominationState(initialState);
    if (R.equals(step, 2)) setStep(1);
    if (view === 'new') {
      navigate('../monthly-nominations/nominated');
    }
  };

  const handleClose = () => {
    if (R.not(R.isNil(copiedNomination))) {
      if (!R.equals(nominationState, initialCopiedNominationState))
        setIsConfirmationDialogOpen(true);
      else handleComplete();

      return;
    }

    if (!R.equals(initialState, nominationState)) setIsConfirmationDialogOpen(true);
    else handleComplete();
  };

  const [createNomination, { isLoading: isCreatingNomination }] = useCreateNominationMutation();
  const { nominationMonth, products, segment, shipper } = nominationState;

  const handleSubmit = () => {
    createNomination({
      body: {
        nominationLegId: R.prop('id', segment),
        periodId: R.prop('id', nominationMonth),
        productNominations: R.reduce(
          (noms, nom) => {
            return [
              ...noms,
              {
                nominationLegProductId: nom.productId,
                volume: R.o(parseInt, R.replace(/[^0-9]/g, ''), nom.volume),
              },
            ];
          },
          [],
          Object.values(products)
        ),
        shipper: {
          companyCustomerIdName: R.prop('customerIdName', shipper),
          dataCompanyId: R.prop('dataCompanyId', shipper),
        },
      },
    })
      .unwrap()
      .then(() => {
        if (creationError) setCreationError('');
      })
      .catch(error => setCreationError(error.data.title));
    setStep(R.inc);
  };

  const handleReset = () => {
    setStep(1);
    setNominationState(initialState);
  };

  const stepProps = {
    copiedNomination,
    errorMessage: creationError,
    isCreatingNomination,
    nominationState,
    onClose: handleClose,
    onComplete: handleComplete,
    onReset: handleReset,
    onSubmit: handleSubmit,
    setInitialCopiedNominationState,
    setNominationState,
  };

  return (
    <Drawer
      anchor="right"
      onClose={R.equals(step, 1) ? handleClose : handleComplete}
      open={isDrawerOpen}
      slotProps={{
        paper: { sx: { width: { lg: 390, xs: '100%' } } },
      }}
    >
      {isDrawerOpen && R.equals(step, 1) && <Form {...stepProps} />}
      {isDrawerOpen && R.equals(step, 2) && <ConfirmationView {...stepProps} />}
      <UnsavedChangesDialog
        handleContinue={() => {
          setIsConfirmationDialogOpen(false);
          handleComplete();
        }}
        handleKeepEditing={() => setIsConfirmationDialogOpen(false)}
        isOpen={isConfirmationDialogOpen}
      />
    </Drawer>
  );
};

CreateNominationWizard.propTypes = {
  copiedNomination: PropTypes.shape({}),
  isDrawerOpen: PropTypes.bool.isRequired,
  setCopiedNomination: PropTypes.func.isRequired,
  setIsDrawerOpen: PropTypes.func.isRequired,
};

CreateNominationWizard.defaultProps = {
  copiedNomination: null,
};

export default CreateNominationWizard;
